import { EnvelopeSimple } from "@phosphor-icons/react"
import { toast } from "react-toastify"

import { Badge, Button } from "~/components/design-system/components"
import { SettingsRow, SettingsSection } from "~/components/design-system/pages"
import { Skeleton } from "~/components/reusable/Skeleton"
import { Tooltip } from "~/components/reusable/Tooltip"
import { trackEvent } from "~/hooks/useTrackingService"
import { UserRole } from "~/providers/userProvider"
import { type Invitation } from "~/server/api/routers/auth0"
import { api } from "~/utils/api"
import { formatDate, isBefore, startOfDayUTC, startOfTodayUTC } from "~/utils/datetime"
import { isProduction } from "~/utils/environment"
import { cn } from "~/utils/helpers"

import { InviteUserDialog } from "./dialogs/InviteUserDialog"
import { RevokeInvitationDialog } from "./dialogs/RevokeInvitationDialog"

interface Props {
  organizationId?: string
  className?: string
  showInviteAction?: boolean
}

const isInvitationExpired = (invitation: Invitation) =>
  isBefore(startOfDayUTC(invitation.expires_at), startOfTodayUTC())

const getInvitationRole = (invitation: Invitation): UserRole =>
  (invitation.roles?.find((role) => Object.values(UserRole).includes(role.name as UserRole))
    ?.name as UserRole) ?? "user"

const isEditingDisabled = (email: string) => !isProduction() && !email.includes("@hakio.com")

const EmptyInvitationsRow = () => (
  <SettingsRow layout="stacked">
    <div className="flex flex-col items-center justify-center gap-2 py-4 text-center">
      <EnvelopeSimple size={32} className="text-fg-disabled" />
      <p className="label-base text-fg-subtle">No pending invitations</p>
      <p className="paragraph-sm text-fg-muted">
        Invite a teammate to give them access to this organization.
      </p>
    </div>
  </SettingsRow>
)

export const InvitationsCard: React.FC<Props> = ({
  organizationId,
  className,
  showInviteAction = true,
}) => {
  const { data: invitations, isLoading } = api.auth0.getInvitations.useQuery({ organizationId })
  const utils = api.useUtils()

  const reinviteUser = api.auth0.inviteUser.useMutation({
    onError: async () => {
      toast.error("Failed to re-invite user")
    },
    onSuccess: async () => {
      toast.success("The user has been re-invited")
      void utils.auth0.getInvitations.invalidate()
    },
  })

  function renderRows() {
    if (isLoading) {
      return (
        <SettingsRow label={<Skeleton className="h-5 w-56" />}>
          <Skeleton className="h-6 w-24" />
        </SettingsRow>
      )
    }
    if (!invitations) return null
    if (invitations.length === 0) return <EmptyInvitationsRow />

    return invitations.map((invitation) => {
      const expired = isInvitationExpired(invitation)
      return (
        <SettingsRow
          key={invitation.invitee.email}
          density="compact"
          label={
            <Tooltip title={"Invited by " + invitation.inviter.name}>
              <span>{invitation.invitee.email}</span>
            </Tooltip>
          }
          description={`${expired ? "Expired" : "Expires"} at: ${formatDate(invitation.expires_at)}`}
        >
          <Badge size="sm" variant={expired ? "expired" : "pending"}>
            {expired ? "Expired" : "Pending"}
          </Badge>
          {expired && (
            <Button
              requires="administer"
              variant="neutral"
              disabled={isEditingDisabled(invitation.invitee.email)}
              loading={reinviteUser.isPending}
              size="sm"
              onClick={() => {
                trackEvent("User Invitation Resent")
                reinviteUser.mutate({
                  email: invitation.invitee.email,
                  role: getInvitationRole(invitation),
                })
              }}
            >
              Resend invitation
            </Button>
          )}
          <RevokeInvitationDialog invitation={invitation} organizationId={organizationId} />
        </SettingsRow>
      )
    })
  }

  const section = (
    <SettingsSection
      title="Pending invitations"
      actions={showInviteAction && <InviteUserDialog organizationId={organizationId} />}
    >
      {renderRows()}
    </SettingsSection>
  )

  // Scroll wrapper only when a caller caps the height; otherwise it clips the card shadow.
  if (!className) return section
  return <div className={cn("min-h-0 overflow-y-auto", className)}>{section}</div>
}
